import { Link } from 'react-router-dom';
import StarField from '../home/components/StarField';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useLang } from '../../i18n/LanguageContext';

const discountTiers = [
  {
    nameEn: 'Foundation Courses',
    nameZh: '基础课程',
    icon: 'ri-seedling-line',
    color: '#c0c0c0',
    gradientFrom: '#c0c0c0',
    gradientTo: '#e8e8e8',
    discountEn: '10% OFF',
    discountZh: '9折',
    descEn: 'Permanent discount on all foundation-level spiritual courses after enrollment.',
    descZh: '完成报名后，所有基础级灵性课程享受永久9折优惠。',
    perks: [
      { en: 'Tarot & Divination series', zh: '塔罗与占卜系列' },
      { en: 'Crystal & Energy basics', zh: '水晶与能量基础' },
      { en: 'Meditation fundamentals', zh: '冥想基础课程' },
      { en: 'Astrology introduction', zh: '占星入门课程' },
    ],
  },
  {
    nameEn: 'Professional Courses',
    nameZh: '专业课程',
    icon: 'ri-vip-crown-line',
    color: '#d4af37',
    gradientFrom: '#d4af37',
    gradientTo: '#f4d03f',
    discountEn: '15% OFF',
    discountZh: '85折',
    descEn: 'Exclusive permanent savings on advanced certification programs for all enrolled students.',
    descZh: '所有在籍学员对进阶认证课程享受永久性85折专属优惠。',
    perks: [
      { en: 'Healing therapy certifications', zh: '疗愈师认证课程' },
      { en: 'Advanced spiritual guidance', zh: '高阶灵性引导' },
      { en: 'Professional intuition training', zh: '专业灵视觉训练' },
      { en: 'Instructor pathway programs', zh: '导师晋升通道课程' },
    ],
    featured: true,
  },
  {
    nameEn: 'Healing Services',
    nameZh: '疗愈服务',
    icon: 'ri-heart-pulse-line',
    color: '#c8b6ff',
    gradientFrom: '#9d78ef',
    gradientTo: '#c8b6ff',
    discountEn: '20% OFF',
    discountZh: '8折',
    descEn: 'All enrolled students enjoy permanent 20% off on private healing consultations and sessions.',
    descZh: '所有在籍学员享受一对一疗愈咨询与私人服务永久8折优惠。',
    perks: [
      { en: '1-on-1 energy healing sessions', zh: '一对一能量疗愈' },
      { en: 'Personal tarot consultations', zh: '个人塔罗咨询' },
      { en: 'Astrology birth chart readings', zh: '星盘个人解读' },
      { en: 'Group healing workshops', zh: '团体疗愈工作坊' },
    ],
  },
];

const highlights = [
  {
    icon: 'ri-infinity-line',
    en: 'Permanently Valid',
    zh: '永久有效',
    descEn: 'Your student discount never expires — enjoy savings for life.',
    descZh: '学员优惠永不过期，一次成为学员，终身享受专属折扣。',
  },
  {
    icon: 'ri-price-tag-3-line',
    en: 'Instant Activation',
    zh: '即时生效',
    descEn: 'Discounts apply automatically upon successful course enrollment.',
    descZh: '成功报名课程后，所有优惠立即自动生效，无需申请。',
  },
  {
    icon: 'ri-stackshere-line',
    en: 'Stackable Savings',
    zh: '叠加优惠',
    descEn: 'Combine student discounts with seasonal promotions for maximum value.',
    descZh: '学员专属折扣可与限时活动折扣叠加使用，省更多。',
  },
  {
    icon: 'ri-global-line',
    en: 'All Formats Covered',
    zh: '全形式适用',
    descEn: 'Live classes, recordings, retreats — discounts apply across all formats.',
    descZh: '直播课、录播课、线下冥想营，全形式均享学员优惠。',
  },
];

const faqs = [
  {
    q: { en: 'How do I qualify for student discounts?', zh: '如何获得学员优惠资格？' },
    a: { en: 'Any student who has successfully enrolled in at least one course at our academy automatically qualifies for lifetime student discounts.', zh: '凡在本学院成功报名至少一门课程的学员，即自动获得终身学员优惠资格。' },
  },
  {
    q: { en: 'Can student discounts be combined after joining the Sisterhood?', zh: '加入姐妹会后，课程优惠可以叠加吗' },
    a: { en: 'Yes, student exclusive discounts can be combined with any other promotional course discounts.', zh: '是的，学员的专属优惠可与任何其他活动中的课程折扣叠加。' },
  },
  {
    q: { en: 'Are offline courses also eligible for discounts?', zh: '线下课程也能享受优惠吗？' },
    a: { en: 'Yes, whether online or offline, all enrolled students enjoy exclusive discounts across every course format.', zh: '是的，无论是线上还是线下的课程，在籍学员都将享受专属优惠。' },
  },
];

export default function MembershipPage() {
  const { t } = useLang();

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-[#1a0b2e] via-[#2d1b4e] to-[#1a0b2e] overflow-hidden">
      <StarField />
      <Navbar />

      {/* Hero */}
      <section className="relative z-10 pt-44 pb-24 px-4 sm:px-8 text-center overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <img
            src="https://public.readdy.ai/ai/img_res/15aee9c97ad43236b934e7afb96ccb43.jpg"
            alt="bg"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a0b2e]/60 via-transparent to-[#1a0b2e]"></div>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-[#d4af37]/15 rounded-full border border-[#d4af37]/30 mb-8">
            <i className="ri-price-tag-3-line text-[#d4af37] text-sm"></i>
            <span className="text-[#d4af37] text-sm font-medium">{t('Exclusive Permanent Benefits', '专属永久性权益')}</span>
          </div>
          <h1 className="text-4xl sm:text-6xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-[#d4af37] via-[#c8b6ff] to-[#d4af37] bg-clip-text text-transparent leading-tight">
            {t('Student Discounts', '学员优惠')}
          </h1>
          <p className="text-lg sm:text-xl text-[#c8b6ff] leading-relaxed mb-10 max-w-2xl mx-auto">
            {t(
              'As an enrolled student, you receive exclusive pricing on all courses.',
              '作为本学院的在籍学员，您将永久享受所有课程的专属优惠'
            )}
          </p>
          <Link
            to="/courses"
            className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-[#d4af37] to-[#f4d03f] rounded-full text-[#1a0b2e] font-bold text-base transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-book-open-line"></i>
            <span>{t('Browse Courses', '浏览课程')}</span>
          </Link>
        </div>
      </section>


      {/* How It Works */}
      <section className="relative z-10 py-20 px-4 sm:px-8">
        <div className="max-w-5xl mx-auto text-center mb-14">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{t('How to Unlock Your Discounts', '如何解锁学员优惠')}</h2>
          <p className="text-[#c8b6ff] text-base">{t('Three simple steps to permanent savings', '三步解锁，终身省钱')}</p>
        </div>
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              step: '01',
              icon: 'ri-book-open-line',
              en: 'Enroll in a Course',
              zh: '报名一门课程',
              descEn: 'Choose any course from our catalogue and complete your enrollment.',
              descZh: '从课程目录中选择任意课程并完成报名。',
            },
            {
              step: '02',
              icon: 'ri-user-star-line',
              en: 'Become a Student',
              zh: '成为在籍学员',
              descEn: 'Your student status is confirmed and discount privileges are activated immediately.',
              descZh: '学员身份确认后，所有专属折扣权益即刻自动激活。',
            },
            {
              step: '03',
              icon: 'ri-infinity-line',
              en: 'Enjoy Forever',
              zh: '永久享受优惠',
              descEn: 'All benefits are permanent, stackable with promotions, and apply to all future courses.',
              descZh: '所有优惠永久有效，可叠加活动，适用于未来所有课程。',
            },
          ].map((s, i) => (
            <div key={s.step} className="relative bg-[#2d1b4e]/60 rounded-2xl p-8 border border-[#d4af37]/20 text-center hover:border-[#d4af37]/40 transition-all duration-300">
              <div className="text-5xl font-black text-[#d4af37]/15 mb-4 leading-none">{s.step}</div>
              <div className="w-12 h-12 mx-auto rounded-full bg-[#d4af37]/15 border border-[#d4af37]/30 flex items-center justify-center mb-4">
                <i className={`${s.icon} text-xl text-[#d4af37]`}></i>
              </div>
              <h3 className="text-white font-bold mb-3">{t(s.en, s.zh)}</h3>
              <p className="text-[#c8b6ff] text-sm leading-relaxed">{t(s.descEn, s.descZh)}</p>
              {i < 2 && (
                <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-6 flex items-center justify-center text-[#d4af37]/50 -translate-y-1/2 z-10">
                  <i className="ri-arrow-right-s-line text-2xl"></i>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Sisterhood Banner */}
      <section className="relative z-10 py-20 px-4 sm:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden border border-[#c8b6ff]/25">
            {/* background image */}
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=three women standing in a mystical sacred circle surrounded by soft glowing candlelight and floating flower petals deep purple indigo background ethereal spiritual feminine energy ancient ritual ceremony dreamy atmospheric photography&width=1200&height=600&seq=sisterhood-banner-bg-001&orientation=landscape"
                alt="Sisterhood"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-[#1a0b2e]/70 via-[#1a0b2e]/75 to-[#1a0b2e]/85 lg:bg-none lg:bg-gradient-to-r lg:from-[#1a0b2e]/92 lg:via-[#2d1b4e]/80 lg:to-[#1a0b2e]/70"></div>
            </div>

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* left: content */}
              <div className="p-10 sm:p-14 flex flex-col justify-center">
                <div className="inline-flex items-center space-x-2 px-3 py-1.5 bg-[#c8b6ff]/15 rounded-full border border-[#c8b6ff]/30 mb-7 w-fit">
                  <i className="ri-moon-foggy-line text-[#c8b6ff] text-sm"></i>
                  <span className="text-[#c8b6ff] text-xs font-medium tracking-wide">{t('Beyond the Course', '更深入的陪伴场域')}</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-5 leading-snug">
                  {t('Join the Sisterhood of the Three Gates', '加入三相之门姐妹会')}
                </h2>
                <p className="text-[#c8b6ff] text-sm leading-relaxed mb-8">
                  {t(
                    'Beyond the course membership system, you can join the Sisterhood of the Three Gates — a continuous field of energy practice, alignment, and mutual support that holds you through your evolving journey.',
                    '除了课程学员体系，您也可以通过加入三相之门姐妹会，进入更持续的能量实践与支持场域，在您不断成长的旅途中持续被托举与陪伴。'
                  )}
                </p>
                <ul className="space-y-3 mb-10">
                  {[
                    { icon: 'ri-price-tag-3-line', en: 'Exclusive student discounts on all academy courses', zh: '学院课程专属折扣' },
                    { icon: 'ri-pulse-line', en: 'Ongoing energy calibration & soul companionship', zh: '持续性的能量校准与灵魂陪伴' },
                    { icon: 'ri-gift-line', en: 'Physical healing tools & cyclical support', zh: '实体疗愈工具与周期性支持' },
                  ].map((item) => (
                    <li key={item.en} className="flex items-start space-x-3">
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className={`${item.icon} text-[#d4af37] text-base`}></i>
                      </div>
                      <span className="text-white text-sm leading-relaxed">{t(item.en, item.zh)}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="http://3gate.readdy.co"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center space-x-2 px-7 py-3.5 bg-gradient-to-r from-[#9d78ef] to-[#c8b6ff] rounded-full text-[#1a0b2e] font-bold text-sm transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap w-fit"
                >
                  <i className="ri-external-link-line"></i>
                  <span>{t('Learn About the Sisterhood', '了解姐妹会')}</span>
                </a>
              </div>

              {/* right: decorative */}
              <div className="flex items-center justify-center p-8 lg:p-14">
                <div className="relative w-36 h-36 lg:w-56 lg:h-56">
                  <div className="absolute inset-0 rounded-full border border-[#c8b6ff]/20 animate-pulse"></div>
                  <div className="absolute inset-4 rounded-full border border-[#d4af37]/20"></div>
                  <div className="absolute inset-8 rounded-full border border-[#c8b6ff]/15"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <i className="ri-moon-foggy-line text-4xl lg:text-5xl text-[#c8b6ff]/70 block mb-2"></i>
                      <p className="text-[#d4af37] text-xs tracking-widest font-medium">三相之门</p>
                      <p className="text-[#c8b6ff]/60 text-xs tracking-widest mt-1">SISTERHOOD</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative z-10 py-20 px-4 sm:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{t('Common Questions', '常见问题')}</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-[#2d1b4e]/60 rounded-2xl p-6 border border-[#d4af37]/20">
                <h4 className="text-white font-semibold mb-2 flex items-start space-x-3">
                  <i className="ri-question-line text-[#d4af37] mt-0.5 flex-shrink-0"></i>
                  <span>{t(faq.q.en, faq.q.zh)}</span>
                </h4>
                <p className="text-[#c8b6ff] text-sm leading-relaxed pl-6">{t(faq.a.en, faq.a.zh)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="relative z-10 py-20 px-4 sm:px-8">
        <div className="max-w-4xl mx-auto bg-gradient-to-r from-[#d4af37]/10 via-[#2d1b4e]/80 to-[#c8b6ff]/10 rounded-3xl border border-[#d4af37]/30 p-12 text-center">
          <i className="ri-gift-line text-5xl text-[#d4af37] mb-6 block"></i>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            {t('Start Your Journey Today', '今天开启您的修行之旅')}
          </h2>
          <p className="text-[#c8b6ff] text-base mb-8 max-w-xl mx-auto leading-relaxed">
            {t(
              'Enroll in your first course and unlock permanent student discounts across all our programs and services — a lifetime of savings starts with one step.',
              '报名第一门课程，即可解锁覆盖所有课程与服务的永久学员优惠——终身省钱，从这一步开始。'
            )}
          </p>
          <Link
            to="/courses"
            className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-[#d4af37] to-[#f4d03f] rounded-full text-[#1a0b2e] font-bold text-base transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-right-up-line"></i>
            <span>{t('View All Courses', '查看全部课程')}</span>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
